import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const ExplorePage = () => {
  const [activeTab, setActiveTab] = useState("discover");
  const [unreadMessages, setUnreadMessages] = useState(2);
  const selectedCity = "montreal";
  const navigate = useNavigate();
  const cityInfo = {
    montreal: {
      name: "Montreal",
      subtitle: "European charm meets North America",
      weather: "24°C",
      weatherDesc: "Sunny",
    },
  };

  const currentCity = cityInfo[selectedCity] || cityInfo.montreal;

  // Quick Tools
  const quickTools = [
    { id: "navigate", icon: "bi-compass", label: "Navigate", color: "#007AFF" },
    { id: "itinerary", icon: "bi-map", label: "Itinerary", color: "#4CAF50" },
    {
      id: "translate",
      icon: "bi-translate",
      label: "Translate",
      color: "#9C27B0",
    },
    { id: "scan", icon: "bi-qr-code-scan", label: "Scan", color: "#FF9800" },
  ];

  // Core Features
  const coreFeatures = [
    {
      id: "trip-plan",
      icon: "bi-calendar-check",
      title: "Trip Planner",
      description: "AI-powered itinerary planner",
      color: "#007AFF",
    },
    {
      id: "self-guide",
      icon: "bi-geo-alt",
      title: "Real-time Guide",
      description: "Navigate like a local",
      color: "#4CAF50",
    },
    {
      id: "community",
      icon: "bi-people",
      title: "Travel Community",
      description: "Connect with travelers",
      color: "#9C27B0",
    },
    {
      id: "deals",
      icon: "bi-lightning-charge",
      title: "Flash Deals",
      description: "Book tickets with discounts",
      color: "#FF6B35",
    },
  ];

  // Hot Spots
  const hotSpots = [
    {
      id: 1,
      name: "Old Montreal",
      category: "Historic",
      categoryColor: "#FF6B6B",
      rating: 4.9,
      reviews: 3,
      description: "European charm in North America",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSLz00hcDlkCi1ohtuqDc2RmiYzNLrp1TBTKQ&s",
    },
    {
      id: 2,
      name: "Notre-Dame Basilica",
      category: "Church",
      categoryColor: "#FF9800",
      rating: 4.7,
      reviews: 89,
      description: "Gothic Revival masterpiece",
      image:
        "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1b/4f/cf/de/interior-from-the-main.jpg?w=900&h=500&s=1",
    },
    {
      id: 3,
      name: "Mount Royal",
      category: "Nature",
      categoryColor: "#4CAF50",
      rating: 4.8,
      reviews: 452,
      description: "City's green heart with stunning views",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTVuGlqygDm-s7O5eZqEDFIBpPL15axY-ZI0w&s",
    },
  ];

  // Services
  const services = [
    {
      id: "restaurants",
      icon: "🍴",
      name: "Restaurants",
      desc: "Local cuisine",
      bgColor: "#FFE8E8",
      color: "#FF6B6B",
    },
    {
      id: "hotels",
      icon: "🏨",
      name: "Hotels",
      desc: "Best stays",
      bgColor: "#E8F5FF",
      color: "#007AFF",
    },
    {
      id: "attractions",
      icon: "🎫",
      name: "Attractions",
      desc: "Must-see spots",
      bgColor: "#FFF3E0",
      color: "#FF9800",
    },
    {
      id: "flights",
      icon: "✈️",
      name: "Flights",
      desc: "Book flights",
      bgColor: "#F3E5F5",
      color: "#9C27B0",
    },
  ];

  const handleFeatureClick = (featureId) => {
    switch (featureId) {
      case "trip-plan":
      case "trip-planner":
        navigate("/trip-planner", {
          state: {
            selectedCity,
            cityName: currentCity.name,
          },
        });
        break;

      case "self-guide":
        window.location.href = "https://citygetaway-frontend.vercel.app/";
        break;

      case "community":
        navigate("/community", {
          state: {
            selectedCity,
          },
        });
        break;

      case "deals":
        navigate("/booking-cart", {
          state: {
            selectedCity,
          },
        });
        break;

      case "messenger":
        navigate("/notifications", {
          state: {
            selectedCity,
          },
        });
        break;

      case "city-eval":
        navigate("/city-reviews", {
          state: {
            selectedCity,
          },
        });
        break;

      default:
        console.log("Unknown feature:", featureId);
    }
  };

  return (
    <>
      <style>{`
        .explore-container {
          width: 100%;
          max-width: 430px;
          height: 100vh;
          margin: 0 auto;
          background: #f7f7f8;
          display: flex;
          flex-direction: column;
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
        }
        
        /* Header */
        .explore-header {
          background: white;
          padding: 50px 20px 20px;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .header-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }
        
        .city-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 24px;
          font-weight: 500;
          color: #1a1a1a;
        }
        
        .weather-info {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #666;
          font-size: 14px;
        }
        
        .weather-icon {
          color: #FFA500;
          font-size: 18px;
        }
        
        .header-actions {
          display: flex;
          gap: 16px;
        }
        
        .header-btn {
          background: none;
          border: none;
          color: #666;
          font-size: 20px;
          cursor: pointer;
          padding: 4px;
        }
        
        /* Content */
        .explore-content {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Quick Tools */
        .quick-tools {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          justify-content: space-between;
        }
        
        .tool-item {
          flex: 1;
          background: white;
          border-radius: 16px;
          padding: 16px 8px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .tool-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        .tool-icon-wrapper {
          width: 48px;
          height: 48px;
          margin: 0 auto 8px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          color: white;
        }
        
        .tool-label {
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }
        
        /* AI Assistant Card */
        .ai-card {
          background: linear-gradient(135deg, #E8D4FF 0%, #F4E8FF 100%);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .ai-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(150, 100, 255, 0.2);
        }
        
        .ai-icon {
          width: 48px;
          height: 48px;
          background: #9C27B0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
        }
        
        .ai-content {
          flex: 1;
        }
        
        .ai-title {
          font-size: 14px;
          color: #666;
          margin-bottom: 4px;
        }
        
        .ai-desc {
          font-size: 16px;
          color: #333;
          font-weight: 500;
        }
        
        .ai-btn {
          padding: 10px 20px;
          background: #9C27B0;
          color: white;
          border: none;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .ai-btn:hover {
          background: #8B24A0;
        }
        
        /* Feature Cards */
        .feature-section {
          margin-bottom: 20px;
        }
        
        .feature-card {
          background: white;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .feature-card:hover {
          transform: translateX(2px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .feature-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .feature-icon-box {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
          flex-shrink: 0;
        }
        
        .feature-info {
          flex: 1;
        }
        
        .feature-title {
          font-size: 16px;
          font-weight: 500;
          color: #1a1a1a;
          margin-bottom: 4px;
        }
        
        .feature-desc {
          font-size: 13px;
          color: #999;
          line-height: 1.4;
        }
        
        .feature-arrow {
          color: #ccc;
          font-size: 18px;
        }
        
        /* Hot Destinations */
        .hot-spots-section {
          margin-bottom: 20px;
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding: 0 4px;
        }
        
        .section-title {
          font-size: 18px;
          font-weight: 500;
          color: #1a1a1a;
        }
        
        .see-all {
          font-size: 13px;
          color: #007AFF;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .spot-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .spot-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        .spot-image {
          width: 100%;
          height: 120px;
          object-fit: cover;
        }
        
        .spot-info {
          padding: 12px;
        }
        
        .spot-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 8px;
        }
        
        .spot-name {
          font-size: 16px;
          font-weight: 500;
          color: #1a1a1a;
        }
        
        .spot-category {
          font-size: 11px;
          color: white;
          padding: 3px 8px;
          border-radius: 6px;
        }
        
        .spot-desc {
          font-size: 13px;
          color: #666;
          margin-bottom: 8px;
        }
        
        .spot-rating {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
        }
        
        .rating-stars {
          color: #FFB800;
        }
        
        .rating-value {
          color: #333;
          font-weight: 500;
        }
        
        /* Shop Card */
        .shop-card {
          background: #FFF9E6;
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .shop-icon {
          width: 48px;
          height: 48px;
          background: #FFB800;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
        }
        
        .shop-info {
          flex: 1;
        }
        
        .shop-title {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }
        
        .shop-subtitle {
          font-size: 12px;
          color: #999;
          margin-bottom: 8px;
        }
        
        .shop-price {
          display: flex;
          align-items: baseline;
          gap: 6px;
        }
        
        .price-original {
          font-size: 12px;
          color: #999;
          text-decoration: line-through;
        }
        
        .price-current {
          font-size: 18px;
          color: #FF6B35;
          font-weight: 600;
        }
        
        .shop-btn {
          padding: 10px 20px;
          background: #FF6B35;
          color: white;
          border: none;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        /* Services Grid */
        .services-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        
        .service-card {
          background: white;
          border-radius: 16px;
          padding: 16px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .service-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        .service-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 8px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }
        
        .service-name {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }
        
        .service-desc {
          font-size: 12px;
          color: #999;
        }
        
        /* Bottom Navigation */
        .bottom-nav {
          background: white;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03);
        }
        
        .nav-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 4px;
          cursor: pointer;
          background: none;
          border: none;
          color: #999;
          flex: 1;
          position: relative;
        }
        
        .nav-item.active {
          color: #007AFF;
        }
        
        .nav-icon {
          font-size: 22px;
          margin-bottom: 4px;
        }
        
        .nav-label {
          font-size: 10px;
        }
        
        .message-badge {
          position: absolute;
          top: -4px;
          right: 50%;
          transform: translateX(10px);
          background: #ff4757;
          color: white;
          font-size: 10px;
          font-weight: 600;
          padding: 2px 5px;
          border-radius: 10px;
          min-width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      <div className="explore-container">
        {/* Header */}
        <div className="explore-header">
          <div className="header-top">
            <div className="city-title">
              <span>{currentCity.name}</span>
              <div className="weather-info">
                <i className="bi bi-sun-fill weather-icon"></i>
                <span>24°</span>
                <span>• {currentCity.weatherDesc}</span>
              </div>
            </div>
            <div className="header-actions">
              <button className="header-btn">
                <i className="bi bi-search"></i>
              </button>
              <button className="header-btn">
                <i className="bi bi-filter"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="explore-content">
          {/* Quick Tools */}
          <div className="quick-tools">
            {quickTools.map((tool) => (
              <div key={tool.id} className="tool-item">
                <div
                  className="tool-icon-wrapper"
                  style={{ background: tool.color }}
                >
                  <i className={`bi ${tool.icon}`}></i>
                </div>
                <div className="tool-label">{tool.label}</div>
              </div>
            ))}
          </div>

          {/* AI Assistant Card */}
          <div
            className="ai-card"
            onClick={() => handleFeatureClick("trip-plan")}
          >
            <div className="ai-icon">
              <i className="bi bi-magic"></i>
            </div>
            <div className="ai-content">
              <div className="ai-title">Hi, I'm Concord 👋</div>
              <div className="ai-desc">Your AI travel assistant</div>
            </div>
            <button
              className="ai-btn"
              onClick={() => handleFeatureClick("trip-plan")}
            >
              Start AI Chat
            </button>
          </div>

          {/* Core Features */}
          <div className="feature-section">
            {coreFeatures.map((feature) => (
              <div
                key={feature.id}
                className="feature-card"
                onClick={() => handleFeatureClick(feature.id)}
              >
                <div className="feature-content">
                  <div
                    className="feature-icon-box"
                    style={{ background: feature.color }}
                  >
                    <i className={`bi ${feature.icon}`}></i>
                  </div>
                  <div className="feature-info">
                    <div className="feature-title">{feature.title}</div>
                    <div className="feature-desc">{feature.description}</div>
                  </div>
                  <i className="bi bi-chevron-right feature-arrow"></i>
                </div>
              </div>
            ))}
          </div>

          {/* Hot Destinations */}
          <div className="hot-spots-section">
            <div className="section-header">
              <h2 className="section-title">Hot Destinations</h2>
              <span className="see-all">
                See all
                <i className="bi bi-chevron-right"></i>
              </span>
            </div>

            {hotSpots.map((spot) => (
              <div key={spot.id} className="spot-card">
                <img src={spot.image} alt={spot.name} className="spot-image" />
                <div className="spot-info">
                  <div className="spot-header">
                    <div className="spot-name">{spot.name}</div>
                    <div
                      className="spot-category"
                      style={{ background: spot.categoryColor }}
                    >
                      {spot.category}
                    </div>
                  </div>
                  <div className="spot-desc">{spot.description}</div>
                  <div className="spot-rating">
                    <span className="rating-stars">★★★★★</span>
                    <span className="rating-value">{spot.rating}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Shop Card */}
          <div className="shop-card">
            <div className="shop-icon">
              <i className="bi bi-shop"></i>
            </div>
            <div className="shop-info">
              <div className="shop-title">City Pass Montreal</div>
              <div className="shop-subtitle">
                Unlimited access to top attractions
              </div>
              <div className="shop-price">
                <span className="price-original">$120</span>
                <span className="price-current">$79/day</span>
              </div>
            </div>
            <button className="shop-btn">Book Now</button>
          </div>

          {/* Services Grid */}
          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card">
                <div
                  className="service-icon"
                  style={{ background: service.bgColor }}
                >
                  <span>{service.icon}</span>
                </div>
                <div className="service-name">{service.name}</div>
                <div className="service-desc">{service.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Navigation with Messages */}
        <div className="bottom-nav">
          <button
            className={`nav-item ${activeTab === "discover" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("discover");
              navigate("/explore", { state: { selectedCity } });
            }}
          >
            <i className="bi bi-compass nav-icon"></i>
            <span className="nav-label">Discover</span>
          </button>

          <button
            className={`nav-item ${activeTab === "plan" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("plan");
              navigate("/trip-planner", {
                state: { selectedCity, cityName: currentCity.name },
              });
            }}
          >
            <i className="bi bi-calendar3 nav-icon"></i>
            <span className="nav-label">Plan</span>
          </button>

          <button
            className={`nav-item ${activeTab === "community" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("community");
              navigate("/community");
            }}
          >
            <i className="bi bi-people nav-icon"></i>
            <span className="nav-label">Community</span>
          </button>

          <button
            className={`nav-item ${activeTab === "bookings" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("bookings");
              navigate("/booking-cart");
            }}
          >
            <i className="bi bi-ticket-perforated nav-icon"></i>
            <span className="nav-label">Bookings</span>
          </button>

          <button
            className={`nav-item ${
              activeTab === "notifications" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("notifications");
              setUnreadMessages(0); // Clear unread count
              navigate("/notifications");
            }}
          >
            <div style={{ position: "relative" }}>
              <i className="bi bi-envelope nav-icon"></i>
              {unreadMessages > 0 && (
                <span className="message-badge">
                  {unreadMessages > 9 ? "9+" : unreadMessages}
                </span>
              )}
            </div>
            <span className="nav-label">Messages</span>
          </button>

          <button
            className={`nav-item ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("profile");
              navigate("/profile", { state: { selectedCity } });
            }}
          >
            <i className="bi bi-person nav-icon"></i>
            <span className="nav-label">Profile</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default ExplorePage;
